/**
 * A photo the sender has picked and the server has already staged. Only the identifier and the
 * thumbnail are kept in the browser — the full-size bytes stay on the server, because ten of them
 * exceed the storage quota of a persisted store.
 */
export interface StagedPhotoDTO {
  id: string;
  thumbnail: string;
  /**
   * When the photo was staged, in epoch milliseconds. Absent in a session persisted before the
   * field existed.
   */
  stagedAt?: number;
}

/**
 * How long a staged photo is assumed to survive when the server has not said.
 * It repeats the configured default of the staging sweep (`photo-stage-task.time-to-live`),
 * and is reached when `ClientInfoDTO` arrives without `stagedPhotoTimeToLiveMs` —
 * a backend too old to serve it — or does not arrive at all.
 * Guessing twelve hours is what keeps a stale thumbnail out of the price:
 * the create form is persisted indefinitely, so a sender returning the next day
 * would otherwise be quoted for photos the sweep has taken and refused at payment.
 */
export const STAGED_PHOTO_TIME_TO_LIVE_MS: number = 12 * 60 * 60 * 1000;

/**
 * Tells whether a staged photo is old enough that the server may already have swept it. A photo
 * carrying no staging time counts as expired, because there is nothing to date it by and a photo
 * the sender has to attach again is a better outcome than a payment the server refuses.
 *
 * @param photo The staged photo.
 * @param timeToLiveMs The period the server keeps a staged photo for, in milliseconds.
 *                     Required rather than defaulted, so that a caller falling back to the constant does so
 *                     where a reader can see it — the period is the server's to decide.
 * @return `true` when the photo can no longer be relied on.
 */
export const isStagedPhotoExpired = (photo: StagedPhotoDTO, timeToLiveMs: number): boolean =>
  !photo.stagedAt || Date.now() - photo.stagedAt >= timeToLiveMs;
